import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { FieldOption } from '@jfh/intake-shared';

/**
 * Searchable dropdown (combobox): a trigger button that opens a filterable list.
 * Used for iQ-backed option lists in the portal, which can be long (e.g. sales
 * reps, currencies, nominal accounts).
 */
export function SearchSelect({
  value,
  options,
  onChange,
  disabled,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  alwaysShow = [],
  ariaLabel,
  className = 'select',
  style,
}: {
  value: string;
  options: FieldOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Option values kept in the list whatever is searched (e.g. "Another…"). */
  alwaysShow?: string[];
  ariaLabel?: string;
  /** The trigger's class and style (it looks like a select by default). */
  className?: string;
  style?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ('');
      inputRef.current?.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) => alwaysShow.includes(o.value) || o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle),
    );
  }, [options, q, alwaysShow]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={className}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left', cursor: 'pointer', ...style }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'inherit' : 'var(--text-3)' }}>
          {selected?.label ?? placeholder}
        </span>
        <span aria-hidden style={{ color: 'var(--text-3)' }}>▾</span>
      </button>
      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            zIndex: 60,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'var(--sh-lg)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
            padding: 0,
          }}
        >
          <input
            ref={inputRef}
            className="input"
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ margin: 8, width: 'auto' }}
          />
          <div style={{ overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 12px', color: 'var(--text-3)', fontSize: 13 }}>No matches</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    border: 'none',
                    background: o.value === value ? 'var(--accent-soft)' : 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: 13.5,
                  }}
                  onMouseEnter={(e) => {
                    if (o.value !== value) e.currentTarget.style.background = 'var(--bg-sunken)';
                  }}
                  onMouseLeave={(e) => {
                    if (o.value !== value) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
